/**
 * Jitsi Meet Tutorial Page
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const translations: Record<Locale, TutorialData> = {
  en: {
    pageTitle: 'Using Sokuji with Jitsi Meet',
    backLink: 'Back to Supported Sites',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Overview',
      content: 'This guide will walk you through setting up and using Sokuji with Jitsi Meet for real-time language translation during your meetings. Jitsi Meet is a free, open-source video conferencing platform that runs entirely in your browser.',
    },
    steps: [
      {
        title: 'Install Sokuji Extension',
        content: 'Make sure you have the Sokuji extension installed in your Chrome or Edge browser. You can get it from the Chrome Web Store.',
        tip: 'The extension icon should appear in your browser toolbar after installation.',
      },
      {
        title: 'Open Jitsi Meet',
        content: 'Navigate to <code>meet.jit.si</code> and create a new meeting room or join an existing one. No account or sign-in is required.',
        tip: 'Jitsi Meet runs entirely in the browser, so there is nothing extra to install on the Jitsi side.',
      },
      {
        title: 'Select the Virtual Microphone',
        content: 'In the Jitsi toolbar, open <strong>Settings → Devices</strong>. Under Microphone, select <strong>Sokuji Virtual Microphone</strong> from the dropdown.',
        tip: 'If the virtual microphone does not appear, refresh the page and ensure the Sokuji extension is active.',
      },
      {
        title: 'Disable Audio Processing',
        content: 'Open <strong>Advanced audio settings</strong> and <strong>turn off Noise suppression, Echo cancellation, and Automatic gain control</strong>. Jitsi enables these by default and they distort the injected translated audio.',
        tip: 'This step is important for Jitsi — leaving these on makes the translated voice sound gated, choppy, or muffled.',
      },
      {
        title: 'Start Speaking',
        content: 'Your speech will now be translated in real-time. Speak naturally and others in the meeting will hear the translated audio.',
        tip: 'Speak at a natural pace for the best translation quality.',
      },
    ],
    tips: {
      title: 'Tips for Best Results',
      items: [
        'Use a good quality headset for clear audio',
        'Disable Noise suppression, Echo cancellation, and Automatic gain control in Jitsi for clear translated audio',
        'Keep sentences concise for more accurate translations',
      ],
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        {
          question: 'Do I need a Jitsi Meet account?',
          answer: 'No. meet.jit.si is free and open-source and does not require an account or sign-in.',
        },
        {
          question: 'Why do I have to disable Noise suppression and Echo cancellation?',
          answer: 'Jitsi processes microphone audio with noise suppression, echo cancellation, and automatic gain control by default. These distort the synthetic translated speech, so turning them off keeps the translated audio clear.',
        },
        {
          question: 'Does Sokuji work with self-hosted Jitsi servers?',
          answer: 'Currently Sokuji supports Jitsi Meet on meet.jit.si. Self-hosted Jitsi on custom domains is not yet supported.',
        },
      ],
    },
    troubleshooting: {
      title: 'Troubleshooting',
      content: 'If you encounter any issues, please visit our <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub repository</a> or contact us at <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> for support.',
    },
  },
  zh: {
    pageTitle: '在 Jitsi Meet 中使用 Sokuji',
    backLink: '返回支持的网站',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概述',
      content: '本指南将指导您如何在 Jitsi Meet 中设置和使用 Sokuji，实现会议中的实时语言翻译。Jitsi Meet 是一个免费的开源视频会议平台，完全在浏览器中运行。',
    },
    steps: [
      {
        title: '安装 Sokuji 扩展',
        content: '确保您已在 Chrome 或 Edge 浏览器中安装了 Sokuji 扩展。您可以从 Chrome 网上应用店获取。',
        tip: '安装后，扩展图标应出现在浏览器工具栏中。',
      },
      {
        title: '打开 Jitsi Meet',
        content: '访问 <code>meet.jit.si</code> 并创建一个新的会议房间或加入现有房间。无需账户或登录。',
        tip: 'Jitsi Meet 完全在浏览器中运行，因此在 Jitsi 一侧无需安装任何额外内容。',
      },
      {
        title: '选择虚拟麦克风',
        content: '在 Jitsi 工具栏中，打开 <strong>设置 → 设备</strong>。在麦克风下，从下拉菜单中选择 <strong>Sokuji Virtual Microphone</strong>。',
        tip: '如果虚拟麦克风没有出现，请刷新页面并确保 Sokuji 扩展处于活动状态。',
      },
      {
        title: '关闭音频处理',
        content: '打开 <strong>高级音频设置</strong>，然后<strong>关闭降噪、回声消除和自动增益控制</strong>。Jitsi 默认启用这些功能，它们会使注入的翻译音频失真。',
        tip: '这一步对 Jitsi 很重要——保持开启会使翻译后的声音听起来断断续续、卡顿或模糊。',
      },
      {
        title: '开始说话',
        content: '您的语音现在将被实时翻译。自然地说话，会议中的其他人将听到翻译后的音频。',
        tip: '以自然的速度说话以获得最佳翻译质量。',
      },
    ],
    tips: {
      title: '最佳使用建议',
      items: [
        '使用高质量耳机以获得清晰的音频',
        '在 Jitsi 中关闭降噪、回声消除和自动增益控制，以获得清晰的翻译音频',
        '保持句子简洁以获得更准确的翻译',
      ],
    },
    faq: {
      title: '常见问题',
      items: [
        {
          question: '我需要 Jitsi Meet 账户吗？',
          answer: '不需要。meet.jit.si 是免费的开源平台，无需账户或登录。',
        },
        {
          question: '为什么我必须关闭降噪和回声消除？',
          answer: 'Jitsi 默认使用降噪、回声消除和自动增益控制来处理麦克风音频。这些会使合成的翻译语音失真，因此关闭它们可以保持翻译音频清晰。',
        },
        {
          question: 'Sokuji 是否适用于自托管的 Jitsi 服务器？',
          answer: '目前 Sokuji 支持 meet.jit.si 上的 Jitsi Meet。尚不支持自定义域名的自托管 Jitsi。',
        },
      ],
    },
    troubleshooting: {
      title: '故障排除',
      content: '如果遇到任何问题，请访问我们的 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 仓库</a> 或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> 获取支持。',
    },
  },
  ja: {
    pageTitle: 'Jitsi Meet で Sokuji を使用する',
    backLink: 'サポートされているサイトに戻る',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概要',
      content: 'このガイドでは、Jitsi Meet で Sokuji を設定して使用し、会議中にリアルタイム言語翻訳を行う方法を説明します。Jitsi Meet は無料のオープンソースのビデオ会議プラットフォームで、完全にブラウザ内で動作します。',
    },
    steps: [
      {
        title: 'Sokuji 拡張機能をインストール',
        content: 'Chrome または Edge ブラウザに Sokuji 拡張機能がインストールされていることを確認してください。Chrome ウェブストアから入手できます。',
        tip: 'インストール後、拡張機能のアイコンがブラウザのツールバーに表示されます。',
      },
      {
        title: 'Jitsi Meet を開く',
        content: '<code>meet.jit.si</code> にアクセスし、新しい会議ルームを作成するか既存のルームに参加します。アカウントやサインインは必要ありません。',
        tip: 'Jitsi Meet は完全にブラウザ内で動作するため、Jitsi 側に追加でインストールするものはありません。',
      },
      {
        title: '仮想マイクを選択',
        content: 'Jitsi のツールバーで <strong>設定 → デバイス</strong> を開きます。マイクの下で、ドロップダウンから <strong>Sokuji Virtual Microphone</strong> を選択します。',
        tip: '仮想マイクが表示されない場合は、ページを更新し、Sokuji 拡張機能がアクティブであることを確認してください。',
      },
      {
        title: 'オーディオ処理を無効にする',
        content: '<strong>詳細なオーディオ設定</strong> を開き、<strong>ノイズ抑制、エコーキャンセル、自動ゲイン制御をオフにします</strong>。Jitsi はこれらをデフォルトで有効にしており、注入された翻訳音声を歪ませます。',
        tip: 'このステップは Jitsi にとって重要です。これらをオンのままにすると、翻訳された音声が途切れたり、ぎくしゃくしたり、こもったりして聞こえます。',
      },
      {
        title: '話し始める',
        content: '音声がリアルタイムで翻訳されるようになります。自然に話すと、会議の他の人は翻訳された音声を聞くことができます。',
        tip: '最適な翻訳品質のために自然なペースで話してください。',
      },
    ],
    tips: {
      title: '最良の結果を得るためのヒント',
      items: [
        'クリアなオーディオのために高品質のヘッドセットを使用する',
        'クリアな翻訳音声のために Jitsi でノイズ抑制、エコーキャンセル、自動ゲイン制御を無効にする',
        'より正確な翻訳のために文を簡潔に保つ',
      ],
    },
    faq: {
      title: 'よくある質問',
      items: [
        {
          question: 'Jitsi Meet のアカウントは必要ですか？',
          answer: 'いいえ。meet.jit.si は無料のオープンソースで、アカウントやサインインは必要ありません。',
        },
        {
          question: 'なぜノイズ抑制とエコーキャンセルを無効にする必要があるのですか？',
          answer: 'Jitsi はデフォルトでノイズ抑制、エコーキャンセル、自動ゲイン制御を使用してマイク音声を処理します。これらは合成された翻訳音声を歪ませるため、オフにすると翻訳音声がクリアに保たれます。',
        },
        {
          question: 'Sokuji はセルフホストの Jitsi サーバーで動作しますか？',
          answer: '現在、Sokuji は meet.jit.si 上の Jitsi Meet をサポートしています。カスタムドメインでのセルフホスト Jitsi はまだサポートされていません。',
        },
      ],
    },
    troubleshooting: {
      title: 'トラブルシューティング',
      content: '問題が発生した場合は、<a href="https://github.com/kizuna-ai-lab/sokuji">GitHub リポジトリ</a>を訪問するか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までメールでお問い合わせください。',
    },
  },
  ko: {
    pageTitle: 'Jitsi Meet에서 Sokuji 사용하기',
    backLink: '지원 사이트로 돌아가기',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '개요',
      content: '이 가이드는 Jitsi Meet에서 Sokuji를 설정하고 사용하여 회의 중 실시간 언어 번역을 수행하는 방법을 안내합니다. Jitsi Meet은 브라우저에서 완전히 실행되는 무료 오픈소스 화상 회의 플랫폼입니다.',
    },
    steps: [
      {
        title: 'Sokuji 확장 프로그램 설치',
        content: 'Chrome 또는 Edge 브라우저에 Sokuji 확장 프로그램이 설치되어 있는지 확인하세요. Chrome 웹 스토어에서 받을 수 있습니다.',
        tip: '설치 후 확장 프로그램 아이콘이 브라우저 도구 모음에 나타납니다.',
      },
      {
        title: 'Jitsi Meet 열기',
        content: '<code>meet.jit.si</code>로 이동하여 새 회의 방을 만들거나 기존 방에 참여하세요. 계정이나 로그인이 필요하지 않습니다.',
        tip: 'Jitsi Meet은 브라우저에서 완전히 실행되므로 Jitsi 측에 추가로 설치할 것이 없습니다.',
      },
      {
        title: '가상 마이크 선택',
        content: 'Jitsi 도구 모음에서 <strong>설정 → 장치</strong>를 엽니다. 마이크에서 드롭다운으로부터 <strong>Sokuji Virtual Microphone</strong>을 선택하세요.',
        tip: '가상 마이크가 나타나지 않으면 페이지를 새로 고치고 Sokuji 확장 프로그램이 활성화되어 있는지 확인하세요.',
      },
      {
        title: '오디오 처리 비활성화',
        content: '<strong>고급 오디오 설정</strong>을 열고 <strong>노이즈 억제, 에코 제거, 자동 게인 제어를 끄세요</strong>. Jitsi는 기본적으로 이를 활성화하며, 주입된 번역 오디오를 왜곡시킵니다.',
        tip: '이 단계는 Jitsi에서 중요합니다. 이를 켜둔 채로 두면 번역된 음성이 끊기거나 답답하게 들립니다.',
      },
      {
        title: '말하기 시작',
        content: '이제 음성이 실시간으로 번역됩니다. 자연스럽게 말하면 회의의 다른 사람들이 번역된 오디오를 듣게 됩니다.',
        tip: '최적의 번역 품질을 위해 자연스러운 속도로 말하세요.',
      },
    ],
    tips: {
      title: '최상의 결과를 위한 팁',
      items: [
        '깨끗한 오디오를 위해 고품질 헤드셋 사용',
        '깨끗한 번역 오디오를 위해 Jitsi에서 노이즈 억제, 에코 제거, 자동 게인 제어를 비활성화',
        '더 정확한 번역을 위해 문장을 간결하게 유지',
      ],
    },
    faq: {
      title: '자주 묻는 질문',
      items: [
        {
          question: 'Jitsi Meet 계정이 필요한가요?',
          answer: '아니요. meet.jit.si는 무료 오픈소스이며 계정이나 로그인이 필요하지 않습니다.',
        },
        {
          question: '노이즈 억제와 에코 제거를 비활성화해야 하는 이유는 무엇인가요?',
          answer: 'Jitsi는 기본적으로 노이즈 억제, 에코 제거, 자동 게인 제어로 마이크 오디오를 처리합니다. 이는 합성된 번역 음성을 왜곡시키므로, 이를 끄면 번역 오디오가 선명하게 유지됩니다.',
        },
        {
          question: 'Sokuji가 자체 호스팅 Jitsi 서버에서 작동하나요?',
          answer: '현재 Sokuji는 meet.jit.si의 Jitsi Meet을 지원합니다. 사용자 지정 도메인의 자체 호스팅 Jitsi는 아직 지원되지 않습니다.',
        },
      ],
    },
    troubleshooting: {
      title: '문제 해결',
      content: '문제가 발생하면 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 저장소</a>를 방문하거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 지원을 받으세요.',
    },
  },
  es: {
    pageTitle: 'Usar Sokuji con Jitsi Meet',
    backLink: 'Volver a sitios compatibles',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Descripción general',
      content: 'Esta guía le explicará cómo configurar y usar Sokuji con Jitsi Meet para la traducción de idiomas en tiempo real durante sus reuniones. Jitsi Meet es una plataforma de videoconferencia gratuita y de código abierto que se ejecuta completamente en su navegador.',
    },
    steps: [
      {
        title: 'Instalar la extensión Sokuji',
        content: 'Asegúrese de tener la extensión Sokuji instalada en su navegador Chrome o Edge. Puede obtenerla en la Chrome Web Store.',
        tip: 'El icono de la extensión debería aparecer en la barra de herramientas de su navegador después de la instalación.',
      },
      {
        title: 'Abrir Jitsi Meet',
        content: 'Navegue a <code>meet.jit.si</code> y cree una nueva sala de reuniones o únase a una existente. No se requiere cuenta ni inicio de sesión.',
        tip: 'Jitsi Meet se ejecuta completamente en el navegador, por lo que no hay nada adicional que instalar en el lado de Jitsi.',
      },
      {
        title: 'Seleccionar el micrófono virtual',
        content: 'En la barra de herramientas de Jitsi, abra <strong>Configuración → Dispositivos</strong>. En Micrófono, seleccione <strong>Sokuji Virtual Microphone</strong> del menú desplegable.',
        tip: 'Si el micrófono virtual no aparece, actualice la página y asegúrese de que la extensión Sokuji esté activa.',
      },
      {
        title: 'Desactivar el procesamiento de audio',
        content: 'Abra <strong>Configuración de audio avanzada</strong> y <strong>desactive la supresión de ruido, la cancelación de eco y el control automático de ganancia</strong>. Jitsi los habilita de forma predeterminada y distorsionan el audio traducido inyectado.',
        tip: 'Este paso es importante para Jitsi: dejarlos activados hace que la voz traducida suene entrecortada, irregular o apagada.',
      },
      {
        title: 'Empezar a hablar',
        content: 'Su voz ahora será traducida en tiempo real. Hable de forma natural y los demás en la reunión escucharán el audio traducido.',
        tip: 'Hable a un ritmo natural para obtener la mejor calidad de traducción.',
      },
    ],
    tips: {
      title: 'Consejos para mejores resultados',
      items: [
        'Use unos auriculares de buena calidad para un audio claro',
        'Desactive la supresión de ruido, la cancelación de eco y el control automático de ganancia en Jitsi para un audio traducido claro',
        'Mantenga las oraciones concisas para traducciones más precisas',
      ],
    },
    faq: {
      title: 'Preguntas frecuentes',
      items: [
        {
          question: '¿Necesito una cuenta de Jitsi Meet?',
          answer: 'No. meet.jit.si es gratuito y de código abierto, y no requiere cuenta ni inicio de sesión.',
        },
        {
          question: '¿Por qué tengo que desactivar la supresión de ruido y la cancelación de eco?',
          answer: 'Jitsi procesa el audio del micrófono con supresión de ruido, cancelación de eco y control automático de ganancia de forma predeterminada. Estos distorsionan el habla traducida sintética, por lo que desactivarlos mantiene el audio traducido claro.',
        },
        {
          question: '¿Funciona Sokuji con servidores Jitsi autoalojados?',
          answer: 'Actualmente Sokuji admite Jitsi Meet en meet.jit.si. Jitsi autoalojado en dominios personalizados aún no es compatible.',
        },
      ],
    },
    troubleshooting: {
      title: 'Solución de problemas',
      content: 'Si encuentra algún problema, visite nuestro <a href="https://github.com/kizuna-ai-lab/sokuji">repositorio de GitHub</a> o contáctenos en <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obtener ayuda.',
    },
  },
  fr: {
    pageTitle: 'Utiliser Sokuji avec Jitsi Meet',
    backLink: 'Retour aux sites pris en charge',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Aperçu',
      content: 'Ce guide vous expliquera comment configurer et utiliser Sokuji avec Jitsi Meet pour la traduction linguistique en temps réel lors de vos réunions. Jitsi Meet est une plateforme de visioconférence gratuite et open source qui fonctionne entièrement dans votre navigateur.',
    },
    steps: [
      {
        title: 'Installer l\'extension Sokuji',
        content: 'Assurez-vous que l\'extension Sokuji est installée dans votre navigateur Chrome ou Edge. Vous pouvez l\'obtenir sur le Chrome Web Store.',
        tip: 'L\'icône de l\'extension devrait apparaître dans la barre d\'outils de votre navigateur après l\'installation.',
      },
      {
        title: 'Ouvrir Jitsi Meet',
        content: 'Accédez à <code>meet.jit.si</code> et créez une nouvelle salle de réunion ou rejoignez-en une existante. Aucun compte ni connexion n\'est requis.',
        tip: 'Jitsi Meet fonctionne entièrement dans le navigateur, il n\'y a donc rien de plus à installer du côté de Jitsi.',
      },
      {
        title: 'Sélectionner le microphone virtuel',
        content: 'Dans la barre d\'outils Jitsi, ouvrez <strong>Paramètres → Périphériques</strong>. Sous Microphone, sélectionnez <strong>Sokuji Virtual Microphone</strong> dans le menu déroulant.',
        tip: 'Si le microphone virtuel n\'apparaît pas, actualisez la page et assurez-vous que l\'extension Sokuji est active.',
      },
      {
        title: 'Désactiver le traitement audio',
        content: 'Ouvrez les <strong>Paramètres audio avancés</strong> et <strong>désactivez la suppression du bruit, l\'annulation d\'écho et le contrôle automatique du gain</strong>. Jitsi les active par défaut et ils déforment l\'audio traduit injecté.',
        tip: 'Cette étape est importante pour Jitsi : les laisser activés rend la voix traduite hachée, saccadée ou étouffée.',
      },
      {
        title: 'Commencer à parler',
        content: 'Votre voix sera maintenant traduite en temps réel. Parlez naturellement et les autres participants à la réunion entendront l\'audio traduit.',
        tip: 'Parlez à un rythme naturel pour une qualité de traduction optimale.',
      },
    ],
    tips: {
      title: 'Conseils pour de meilleurs résultats',
      items: [
        'Utilisez un casque de bonne qualité pour un audio clair',
        'Désactivez la suppression du bruit, l\'annulation d\'écho et le contrôle automatique du gain dans Jitsi pour un audio traduit clair',
        'Gardez les phrases concises pour des traductions plus précises',
      ],
    },
    faq: {
      title: 'Questions fréquemment posées',
      items: [
        {
          question: 'Ai-je besoin d\'un compte Jitsi Meet ?',
          answer: 'Non. meet.jit.si est gratuit et open source et ne nécessite aucun compte ni connexion.',
        },
        {
          question: 'Pourquoi dois-je désactiver la suppression du bruit et l\'annulation d\'écho ?',
          answer: 'Jitsi traite l\'audio du microphone avec la suppression du bruit, l\'annulation d\'écho et le contrôle automatique du gain par défaut. Ceux-ci déforment la parole traduite synthétique, donc les désactiver garde l\'audio traduit clair.',
        },
        {
          question: 'Sokuji fonctionne-t-il avec des serveurs Jitsi auto-hébergés ?',
          answer: 'Actuellement, Sokuji prend en charge Jitsi Meet sur meet.jit.si. Jitsi auto-hébergé sur des domaines personnalisés n\'est pas encore pris en charge.',
        },
      ],
    },
    troubleshooting: {
      title: 'Dépannage',
      content: 'Si vous rencontrez des problèmes, veuillez visiter notre <a href="https://github.com/kizuna-ai-lab/sokuji">dépôt GitHub</a> ou nous contacter à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> pour obtenir de l\'aide.',
    },
  },
  ar: {
    pageTitle: 'استخدام Sokuji مع Jitsi Meet',
    backLink: 'العودة إلى المواقع المدعومة',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'نظرة عامة',
      content: 'سيرشدك هذا الدليل خلال إعداد واستخدام Sokuji مع Jitsi Meet للترجمة اللغوية الفورية أثناء اجتماعاتك. Jitsi Meet هي منصة مؤتمرات فيديو مجانية ومفتوحة المصدر تعمل بالكامل في متصفحك.',
    },
    steps: [
      {
        title: 'تثبيت إضافة Sokuji',
        content: 'تأكد من تثبيت إضافة Sokuji في متصفح Chrome أو Edge. يمكنك الحصول عليها من سوق Chrome الإلكتروني.',
        tip: 'يجب أن يظهر رمز الإضافة في شريط أدوات المتصفح بعد التثبيت.',
      },
      {
        title: 'فتح Jitsi Meet',
        content: 'انتقل إلى <code>meet.jit.si</code> وأنشئ غرفة اجتماع جديدة أو انضم إلى غرفة موجودة. لا يلزم وجود حساب أو تسجيل دخول.',
        tip: 'يعمل Jitsi Meet بالكامل في المتصفح، لذا لا يوجد شيء إضافي لتثبيته من جانب Jitsi.',
      },
      {
        title: 'اختيار الميكروفون الافتراضي',
        content: 'في شريط أدوات Jitsi، افتح <strong>الإعدادات → الأجهزة</strong>. تحت الميكروفون، اختر <strong>Sokuji Virtual Microphone</strong> من القائمة المنسدلة.',
        tip: 'إذا لم يظهر الميكروفون الافتراضي، قم بتحديث الصفحة وتأكد من أن إضافة Sokuji نشطة.',
      },
      {
        title: 'تعطيل معالجة الصوت',
        content: 'افتح <strong>إعدادات الصوت المتقدمة</strong> و<strong>قم بإيقاف تشغيل كبح الضوضاء وإلغاء الصدى والتحكم التلقائي في الكسب</strong>. يقوم Jitsi بتمكين هذه الميزات افتراضيًا وهي تشوه الصوت المترجم المحقون.',
        tip: 'هذه الخطوة مهمة لـ Jitsi - تركها قيد التشغيل يجعل الصوت المترجم يبدو متقطعًا أو مكتومًا.',
      },
      {
        title: 'ابدأ بالتحدث',
        content: 'سيتم الآن ترجمة كلامك في الوقت الفعلي. تحدث بشكل طبيعي وسيسمع الآخرون في الاجتماع الصوت المترجم.',
        tip: 'تحدث بوتيرة طبيعية للحصول على أفضل جودة ترجمة.',
      },
    ],
    tips: {
      title: 'نصائح للحصول على أفضل النتائج',
      items: [
        'استخدم سماعات رأس عالية الجودة للحصول على صوت واضح',
        'قم بتعطيل كبح الضوضاء وإلغاء الصدى والتحكم التلقائي في الكسب في Jitsi للحصول على صوت مترجم واضح',
        'حافظ على الجمل مختصرة لترجمات أكثر دقة',
      ],
    },
    faq: {
      title: 'الأسئلة الشائعة',
      items: [
        {
          question: 'هل أحتاج إلى حساب Jitsi Meet؟',
          answer: 'لا. meet.jit.si مجاني ومفتوح المصدر ولا يتطلب حسابًا أو تسجيل دخول.',
        },
        {
          question: 'لماذا يجب أن أعطل كبح الضوضاء وإلغاء الصدى؟',
          answer: 'يعالج Jitsi صوت الميكروفون باستخدام كبح الضوضاء وإلغاء الصدى والتحكم التلقائي في الكسب افتراضيًا. هذه تشوه الكلام المترجم الاصطناعي، لذا فإن إيقاف تشغيلها يبقي الصوت المترجم واضحًا.',
        },
        {
          question: 'هل يعمل Sokuji مع خوادم Jitsi المستضافة ذاتيًا؟',
          answer: 'حاليًا، يدعم Sokuji خدمة Jitsi Meet على meet.jit.si. لا يتم دعم Jitsi المستضاف ذاتيًا على نطاقات مخصصة بعد.',
        },
      ],
    },
    troubleshooting: {
      title: 'استكشاف الأخطاء وإصلاحها',
      content: 'إذا واجهت أي مشاكل، يرجى زيارة <a href="https://github.com/kizuna-ai-lab/sokuji">مستودع GitHub</a> الخاص بنا أو التواصل معنا عبر <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> للحصول على الدعم.',
    },
  },
  pt: {
    pageTitle: 'Usando o Sokuji com o Jitsi Meet',
    backLink: 'Voltar para sites compatíveis',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Visão geral',
      content: 'Este guia vai orientá-lo na configuração e uso do Sokuji com o Jitsi Meet para tradução de idiomas em tempo real durante suas reuniões. O Jitsi Meet é uma plataforma de videoconferência gratuita e de código aberto que funciona inteiramente no seu navegador.',
    },
    steps: [
      {
        title: 'Instalar a extensão Sokuji',
        content: 'Certifique-se de que a extensão Sokuji está instalada no seu navegador Chrome ou Edge. Você pode obtê-la na Chrome Web Store.',
        tip: 'O ícone da extensão deve aparecer na barra de ferramentas do seu navegador após a instalação.',
      },
      {
        title: 'Abrir o Jitsi Meet',
        content: 'Navegue até <code>meet.jit.si</code> e crie uma nova sala de reunião ou entre em uma existente. Não é necessária conta nem login.',
        tip: 'O Jitsi Meet funciona inteiramente no navegador, então não há nada extra para instalar do lado do Jitsi.',
      },
      {
        title: 'Selecionar o microfone virtual',
        content: 'Na barra de ferramentas do Jitsi, abra <strong>Configurações → Dispositivos</strong>. Em Microfone, selecione <strong>Sokuji Virtual Microphone</strong> no menu suspenso.',
        tip: 'Se o microfone virtual não aparecer, atualize a página e certifique-se de que a extensão Sokuji está ativa.',
      },
      {
        title: 'Desativar o processamento de áudio',
        content: 'Abra as <strong>Configurações avançadas de áudio</strong> e <strong>desative a supressão de ruído, o cancelamento de eco e o controle automático de ganho</strong>. O Jitsi os ativa por padrão e eles distorcem o áudio traduzido injetado.',
        tip: 'Esta etapa é importante para o Jitsi: deixá-los ativados faz com que a voz traduzida soe entrecortada, irregular ou abafada.',
      },
      {
        title: 'Começar a falar',
        content: 'Sua fala agora será traduzida em tempo real. Fale naturalmente e os outros na reunião ouvirão o áudio traduzido.',
        tip: 'Fale em um ritmo natural para obter a melhor qualidade de tradução.',
      },
    ],
    tips: {
      title: 'Dicas para melhores resultados',
      items: [
        'Use um headset de boa qualidade para áudio claro',
        'Desative a supressão de ruído, o cancelamento de eco e o controle automático de ganho no Jitsi para um áudio traduzido claro',
        'Mantenha as frases concisas para traduções mais precisas',
      ],
    },
    faq: {
      title: 'Perguntas frequentes',
      items: [
        {
          question: 'Preciso de uma conta do Jitsi Meet?',
          answer: 'Não. O meet.jit.si é gratuito e de código aberto e não requer conta nem login.',
        },
        {
          question: 'Por que tenho que desativar a supressão de ruído e o cancelamento de eco?',
          answer: 'O Jitsi processa o áudio do microfone com supressão de ruído, cancelamento de eco e controle automático de ganho por padrão. Estes distorcem a fala traduzida sintética, então desativá-los mantém o áudio traduzido claro.',
        },
        {
          question: 'O Sokuji funciona com servidores Jitsi auto-hospedados?',
          answer: 'Atualmente o Sokuji suporta o Jitsi Meet em meet.jit.si. O Jitsi auto-hospedado em domínios personalizados ainda não é compatível.',
        },
      ],
    },
    troubleshooting: {
      title: 'Solução de problemas',
      content: 'Se encontrar algum problema, visite nosso <a href="https://github.com/kizuna-ai-lab/sokuji">repositório no GitHub</a> ou entre em contato conosco em <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obter suporte.',
    },
  },
  ru: {
    pageTitle: 'Использование Sokuji с Jitsi Meet',
    backLink: 'Назад к поддерживаемым сайтам',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Обзор',
      content: 'Это руководство поможет вам настроить и использовать Sokuji с Jitsi Meet для перевода в реальном времени во время ваших встреч. Jitsi Meet — это бесплатная платформа видеоконференций с открытым исходным кодом, которая работает полностью в вашем браузере.',
    },
    steps: [
      {
        title: 'Установите расширение Sokuji',
        content: 'Убедитесь, что расширение Sokuji установлено в вашем браузере Chrome или Edge. Вы можете получить его в Интернет-магазине Chrome.',
        tip: 'После установки значок расширения должен появиться на панели инструментов браузера.',
      },
      {
        title: 'Откройте Jitsi Meet',
        content: 'Перейдите на <code>meet.jit.si</code> и создайте новую комнату для встречи или присоединитесь к существующей. Учётная запись или вход не требуются.',
        tip: 'Jitsi Meet работает полностью в браузере, поэтому на стороне Jitsi не нужно устанавливать ничего дополнительного.',
      },
      {
        title: 'Выберите виртуальный микрофон',
        content: 'На панели инструментов Jitsi откройте <strong>Настройки → Устройства</strong>. В разделе Микрофон выберите <strong>Sokuji Virtual Microphone</strong> из выпадающего меню.',
        tip: 'Если виртуальный микрофон не отображается, обновите страницу и убедитесь, что расширение Sokuji активно.',
      },
      {
        title: 'Отключите обработку звука',
        content: 'Откройте <strong>Расширенные настройки звука</strong> и <strong>отключите шумоподавление, эхоподавление и автоматическую регулировку усиления</strong>. Jitsi включает их по умолчанию, и они искажают вставляемый переведённый звук.',
        tip: 'Этот шаг важен для Jitsi: если оставить их включёнными, переведённый голос будет звучать прерывисто, рвано или приглушённо.',
      },
      {
        title: 'Начните говорить',
        content: 'Теперь ваша речь будет переводиться в реальном времени. Говорите естественно, и остальные участники встречи услышат переведённый звук.',
        tip: 'Говорите в естественном темпе для оптимального качества перевода.',
      },
    ],
    tips: {
      title: 'Советы для лучших результатов',
      items: [
        'Используйте качественную гарнитуру для чистого звука',
        'Отключите шумоподавление, эхоподавление и автоматическую регулировку усиления в Jitsi для чистого переведённого звука',
        'Формулируйте предложения кратко для более точного перевода',
      ],
    },
    faq: {
      title: 'Часто задаваемые вопросы',
      items: [
        {
          question: 'Нужна ли мне учётная запись Jitsi Meet?',
          answer: 'Нет. meet.jit.si бесплатен и имеет открытый исходный код, он не требует учётной записи или входа.',
        },
        {
          question: 'Почему я должен отключать шумоподавление и эхоподавление?',
          answer: 'По умолчанию Jitsi обрабатывает звук микрофона с помощью шумоподавления, эхоподавления и автоматической регулировки усиления. Они искажают синтезированную переведённую речь, поэтому их отключение сохраняет переведённый звук чистым.',
        },
        {
          question: 'Работает ли Sokuji с самостоятельно размещёнными серверами Jitsi?',
          answer: 'В настоящее время Sokuji поддерживает Jitsi Meet на meet.jit.si. Самостоятельно размещённый Jitsi на пользовательских доменах пока не поддерживается.',
        },
      ],
    },
    troubleshooting: {
      title: 'Устранение неполадок',
      content: 'Если вы столкнулись с проблемами, посетите наш <a href="https://github.com/kizuna-ai-lab/sokuji">репозиторий на GitHub</a> или свяжитесь с нами по адресу <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> для получения поддержки.',
    },
  },
  de: {
    pageTitle: 'Sokuji mit Jitsi Meet verwenden',
    backLink: 'Zurück zu unterstützten Seiten',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Überblick',
      content: 'Diese Anleitung führt Sie durch die Einrichtung und Verwendung von Sokuji mit Jitsi Meet für Echtzeit-Sprachübersetzung während Ihrer Meetings. Jitsi Meet ist eine kostenlose Open-Source-Videokonferenzplattform, die vollständig in Ihrem Browser läuft.',
    },
    steps: [
      {
        title: 'Sokuji-Erweiterung installieren',
        content: 'Stellen Sie sicher, dass die Sokuji-Erweiterung in Ihrem Chrome- oder Edge-Browser installiert ist. Sie können sie im Chrome Web Store erhalten.',
        tip: 'Das Symbol der Erweiterung sollte nach der Installation in der Symbolleiste Ihres Browsers erscheinen.',
      },
      {
        title: 'Jitsi Meet öffnen',
        content: 'Navigieren Sie zu <code>meet.jit.si</code> und erstellen Sie einen neuen Meetingraum oder treten Sie einem bestehenden bei. Es ist kein Konto und keine Anmeldung erforderlich.',
        tip: 'Jitsi Meet läuft vollständig im Browser, daher muss auf der Jitsi-Seite nichts zusätzlich installiert werden.',
      },
      {
        title: 'Virtuelles Mikrofon auswählen',
        content: 'Öffnen Sie in der Jitsi-Symbolleiste <strong>Einstellungen → Geräte</strong>. Wählen Sie unter Mikrofon <strong>Sokuji Virtual Microphone</strong> aus dem Dropdown-Menü.',
        tip: 'Wenn das virtuelle Mikrofon nicht angezeigt wird, aktualisieren Sie die Seite und stellen Sie sicher, dass die Sokuji-Erweiterung aktiv ist.',
      },
      {
        title: 'Audioverarbeitung deaktivieren',
        content: 'Öffnen Sie die <strong>Erweiterten Audioeinstellungen</strong> und <strong>deaktivieren Sie Rauschunterdrückung, Echounterdrückung und automatische Verstärkungsregelung</strong>. Jitsi aktiviert diese standardmäßig und sie verzerren das eingespeiste übersetzte Audio.',
        tip: 'Dieser Schritt ist für Jitsi wichtig: Bleiben sie aktiviert, klingt die übersetzte Stimme abgehackt, stockend oder dumpf.',
      },
      {
        title: 'Sprechen Sie los',
        content: 'Ihre Sprache wird jetzt in Echtzeit übersetzt. Sprechen Sie natürlich und die anderen im Meeting werden das übersetzte Audio hören.',
        tip: 'Sprechen Sie in einem natürlichen Tempo für optimale Übersetzungsqualität.',
      },
    ],
    tips: {
      title: 'Tipps für beste Ergebnisse',
      items: [
        'Verwenden Sie ein hochwertiges Headset für klaren Ton',
        'Deaktivieren Sie Rauschunterdrückung, Echounterdrückung und automatische Verstärkungsregelung in Jitsi für klares übersetztes Audio',
        'Halten Sie Sätze kurz und prägnant für genauere Übersetzungen',
      ],
    },
    faq: {
      title: 'Häufig gestellte Fragen',
      items: [
        {
          question: 'Benötige ich ein Jitsi-Meet-Konto?',
          answer: 'Nein. meet.jit.si ist kostenlos und Open Source und erfordert kein Konto und keine Anmeldung.',
        },
        {
          question: 'Warum muss ich Rauschunterdrückung und Echounterdrückung deaktivieren?',
          answer: 'Jitsi verarbeitet das Mikrofon-Audio standardmäßig mit Rauschunterdrückung, Echounterdrückung und automatischer Verstärkungsregelung. Diese verzerren die synthetische übersetzte Sprache, sodass das Deaktivieren das übersetzte Audio klar hält.',
        },
        {
          question: 'Funktioniert Sokuji mit selbst gehosteten Jitsi-Servern?',
          answer: 'Derzeit unterstützt Sokuji Jitsi Meet auf meet.jit.si. Selbst gehostetes Jitsi auf benutzerdefinierten Domains wird noch nicht unterstützt.',
        },
      ],
    },
    troubleshooting: {
      title: 'Fehlerbehebung',
      content: 'Wenn Sie auf Probleme stoßen, besuchen Sie bitte unser <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub-Repository</a> oder kontaktieren Sie uns unter <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> für Unterstützung.',
    },
  },
  uk: {
    pageTitle: 'Використання Sokuji з Jitsi Meet',
    backLink: 'Назад до підтримуваних сайтів',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Огляд',
      content: 'Цей посібник допоможе вам налаштувати та використовувати Sokuji з Jitsi Meet для перекладу мов у реальному часі під час ваших зустрічей. Jitsi Meet — це безкоштовна платформа відеоконференцій з відкритим кодом, яка працює повністю у вашому браузері.',
    },
    steps: [
      {
        title: 'Встановіть розширення Sokuji',
        content: 'Переконайтеся, що розширення Sokuji встановлено у вашому браузері Chrome або Edge. Ви можете отримати його в Інтернет-магазині Chrome.',
        tip: 'Після встановлення значок розширення повинен з\'явитися на панелі інструментів браузера.',
      },
      {
        title: 'Відкрийте Jitsi Meet',
        content: 'Перейдіть на <code>meet.jit.si</code> та створіть нову кімнату для зустрічі або приєднайтеся до існуючої. Обліковий запис або вхід не потрібні.',
        tip: 'Jitsi Meet працює повністю у браузері, тому на стороні Jitsi не потрібно встановлювати нічого додаткового.',
      },
      {
        title: 'Виберіть віртуальний мікрофон',
        content: 'На панелі інструментів Jitsi відкрийте <strong>Налаштування → Пристрої</strong>. У розділі Мікрофон виберіть <strong>Sokuji Virtual Microphone</strong> зі спадного меню.',
        tip: 'Якщо віртуальний мікрофон не з\'являється, оновіть сторінку та переконайтеся, що розширення Sokuji активне.',
      },
      {
        title: 'Вимкніть обробку звуку',
        content: 'Відкрийте <strong>Розширені налаштування звуку</strong> та <strong>вимкніть придушення шуму, усунення відлуння та автоматичне регулювання підсилення</strong>. Jitsi вмикає їх за замовчуванням, і вони спотворюють вставлений перекладений звук.',
        tip: 'Цей крок важливий для Jitsi: якщо залишити їх увімкненими, перекладений голос звучатиме переривчасто, рвано або приглушено.',
      },
      {
        title: 'Почніть говорити',
        content: 'Тепер ваше мовлення буде перекладатися в реальному часі. Говоріть природно, і інші учасники зустрічі почують перекладене аудіо.',
        tip: 'Говоріть у природному темпі для оптимальної якості перекладу.',
      },
    ],
    tips: {
      title: 'Поради для кращих результатів',
      items: [
        'Використовуйте якісну гарнітуру для чистого звуку',
        'Вимкніть придушення шуму, усунення відлуння та автоматичне регулювання підсилення в Jitsi для чистого перекладеного звуку',
        'Формулюйте речення стисло для точнішого перекладу',
      ],
    },
    faq: {
      title: 'Часті запитання',
      items: [
        {
          question: 'Чи потрібен мені обліковий запис Jitsi Meet?',
          answer: 'Ні. meet.jit.si безкоштовний і має відкритий код, він не вимагає облікового запису чи входу.',
        },
        {
          question: 'Чому я маю вимикати придушення шуму та усунення відлуння?',
          answer: 'За замовчуванням Jitsi обробляє звук мікрофона за допомогою придушення шуму, усунення відлуння та автоматичного регулювання підсилення. Вони спотворюють синтезоване перекладене мовлення, тому їх вимкнення зберігає перекладений звук чистим.',
        },
        {
          question: 'Чи працює Sokuji з самостійно розміщеними серверами Jitsi?',
          answer: 'Наразі Sokuji підтримує Jitsi Meet на meet.jit.si. Самостійно розміщений Jitsi на власних доменах поки що не підтримується.',
        },
      ],
    },
    troubleshooting: {
      title: 'Усунення несправностей',
      content: 'Якщо у вас виникли проблеми, відвідайте наш <a href="https://github.com/kizuna-ai-lab/sokuji">репозиторій на GitHub</a> або зв\'яжіться з нами за адресою <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> для отримання підтримки.',
    },
  },
  it: {
    pageTitle: 'Usare Sokuji con Jitsi Meet',
    backLink: 'Torna ai siti supportati',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Panoramica',
      content: 'Questa guida ti spiegherà come configurare e utilizzare Sokuji con Jitsi Meet per la traduzione linguistica in tempo reale durante le tue riunioni. Jitsi Meet è una piattaforma di videoconferenza gratuita e open source che funziona interamente nel tuo browser.',
    },
    steps: [
      {
        title: 'Installare l\'estensione Sokuji',
        content: 'Assicurati di avere l\'estensione Sokuji installata nel tuo browser Chrome o Edge. Puoi ottenerla dal Chrome Web Store.',
        tip: 'L\'icona dell\'estensione dovrebbe apparire nella barra degli strumenti del browser dopo l\'installazione.',
      },
      {
        title: 'Aprire Jitsi Meet',
        content: 'Vai su <code>meet.jit.si</code> e crea una nuova stanza per riunioni o unisciti a una esistente. Non è richiesto alcun account né accesso.',
        tip: 'Jitsi Meet funziona interamente nel browser, quindi non c\'è nulla di aggiuntivo da installare sul lato Jitsi.',
      },
      {
        title: 'Selezionare il microfono virtuale',
        content: 'Nella barra degli strumenti di Jitsi, apri <strong>Impostazioni → Dispositivi</strong>. Sotto Microfono, seleziona <strong>Sokuji Virtual Microphone</strong> dal menu a discesa.',
        tip: 'Se il microfono virtuale non appare, aggiorna la pagina e assicurati che l\'estensione Sokuji sia attiva.',
      },
      {
        title: 'Disattivare l\'elaborazione audio',
        content: 'Apri le <strong>Impostazioni audio avanzate</strong> e <strong>disattiva la soppressione del rumore, la cancellazione dell\'eco e il controllo automatico del guadagno</strong>. Jitsi li abilita per impostazione predefinita e distorcono l\'audio tradotto iniettato.',
        tip: 'Questo passaggio è importante per Jitsi: lasciarli attivi fa sembrare la voce tradotta spezzettata, irregolare o ovattata.',
      },
      {
        title: 'Iniziare a parlare',
        content: 'Il tuo discorso verrà ora tradotto in tempo reale. Parla naturalmente e gli altri nella riunione sentiranno l\'audio tradotto.',
        tip: 'Parla a un ritmo naturale per una qualità di traduzione ottimale.',
      },
    ],
    tips: {
      title: 'Consigli per i migliori risultati',
      items: [
        'Usa un headset di buona qualità per un audio chiaro',
        'Disattiva la soppressione del rumore, la cancellazione dell\'eco e il controllo automatico del guadagno in Jitsi per un audio tradotto chiaro',
        'Mantieni le frasi concise per traduzioni più accurate',
      ],
    },
    faq: {
      title: 'Domande frequenti',
      items: [
        {
          question: 'Ho bisogno di un account Jitsi Meet?',
          answer: 'No. meet.jit.si è gratuito e open source e non richiede account né accesso.',
        },
        {
          question: 'Perché devo disattivare la soppressione del rumore e la cancellazione dell\'eco?',
          answer: 'Jitsi elabora l\'audio del microfono con soppressione del rumore, cancellazione dell\'eco e controllo automatico del guadagno per impostazione predefinita. Questi distorcono il parlato tradotto sintetico, quindi disattivarli mantiene l\'audio tradotto chiaro.',
        },
        {
          question: 'Sokuji funziona con server Jitsi self-hosted?',
          answer: 'Attualmente Sokuji supporta Jitsi Meet su meet.jit.si. Jitsi self-hosted su domini personalizzati non è ancora supportato.',
        },
      ],
    },
    troubleshooting: {
      title: 'Risoluzione dei problemi',
      content: 'Se riscontri problemi, visita il nostro <a href="https://github.com/kizuna-ai-lab/sokuji">repository GitHub</a> o contattaci all\'indirizzo <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> per ricevere assistenza.',
    },
  },
};

export function JitsiTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/jitsi" />;
}
